import { getPresignedUrl, IFileUpload, uploadToStorage } from "@/api";
import { FileError } from "@/config/constants";
import { styles } from "@/config/project";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { filesize } from "filesize";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { DeleteUpload } from "./DeleteUpload";

interface IProps {
    /**
     * The name of the file field in the form.
     */
    name: string;
}

/**
 * Handles the upload of a file to S3.
 */
export const UploadFile = ({ name }: IProps) => {
    const formContext = useFormContext();
    const hasFormContext = !!formContext;

    const { setValue, watch, setError } = formContext || { formState: {} };

    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const {
        mutate: uploadFile,
        isPending: isUploading,
        isError,
    } = useMutation({
        mutationKey: ["fileUpload"],
        mutationFn: async ({
            fileUpload,
            file,
        }: {
            fileUpload: IFileUpload;
            file: File;
        }) => {
            setUploadProgress(0);
            const presignedUrl = await getPresignedUrl(fileUpload);
            const objectName = await uploadToStorage(
                file,
                presignedUrl,
                (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                }
            );
            return objectName;
        },
        onSuccess: (objectName) => {
            setValue("objectName", objectName);
            setUploadProgress(null);
        },
        onError: () => {
            setUploadProgress(null);
            setError("objectName", {
                type: FileError.UploadFailed,
                message: "File upload failed. ",
            });
        },
    });

    const file = hasFormContext ? watch(name) : undefined;
    const objectName = hasFormContext ? watch("objectName") : undefined;

    useEffect(() => {
        // If there is a file, but no object name, it's safe to assume that the file hasn't been uploaded yet
        if (file && !objectName) {
            uploadFile({
                fileUpload: {
                    filename: file.name,
                    content_type: file.type,
                },
                file,
            });
        }
    }, [file, objectName, uploadFile]);

    if (!file) {
        return null;
    }

    const removeFile = () => {
        setValue(name, undefined);
        setValue("objectName", undefined);
    };

    return (
        <Stack
            width={"100%"}
            direction={"row"}
            gap={3}
            alignItems={"center"}
            p={2}
        >
            <UploadFileIcon
                sx={{
                    width: 24,
                    height: 24,
                    color: styles.blue,
                    mb: 1,
                }}
            />

            <Stack>
                <Typography>{file.name}</Typography>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                    <Typography variant={"body2"} color={"textSecondary"}>
                        {filesize(file.size, { round: 1 })}
                    </Typography>
                    {isUploading &&
                        typeof uploadProgress === "number" &&
                        !isError && (
                            <>
                                <Typography
                                    color={"textSecondary"}
                                    variant={"body2"}
                                >
                                    •
                                </Typography>
                                <Typography
                                    color={"textSecondary"}
                                    variant={"body2"}
                                >{`${uploadProgress}%`}</Typography>
                            </>
                        )}
                    {isError && (
                        <>
                            <Typography
                                color={"textSecondary"}
                                variant={"body2"}
                            >
                                •
                            </Typography>
                            <ErrorOutlineIcon
                                sx={{ width: 16, height: 16, mr: -0.5 }}
                                color={"error"}
                            />
                            <Typography color={"error"} variant={"body2"}>
                                Failed to upload
                            </Typography>
                        </>
                    )}
                </Stack>
                {(isUploading || isError) && (
                    <LinearProgress
                        sx={{ width: 200, borderRadius: 8, mt: 0.5 }}
                        variant={"determinate"}
                        value={uploadProgress ?? 0}
                        color={isError ? "error" : "primary"}
                    />
                )}
            </Stack>

            <Box flexGrow={1} />

            {(!isUploading || isError) && (
                <DeleteUpload onDelete={removeFile} />
            )}
        </Stack>
    );
};
